import { useState, useEffect } from 'react';
import api from '../services/api';

export interface ThemeType {
  id: number;
  key: string;
  name: string;
  description: string;
  icon: string;
  gradient: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const useThemeTypes = () => {
  const [themeTypes, setThemeTypes] = useState<ThemeType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchThemeTypes = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Adicionar timestamp para evitar cache do browser
      const timestamp = new Date().getTime();
      const response = await api.get(`/theme-types/active`, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        params: { t: timestamp }
      });

      const data = response.data;
      setThemeTypes(data.theme_types || []);
    } catch (err) {
      console.error('Erro ao buscar tipos de tema:', err);
      setError(err instanceof Error ? err.message : 'Erro desconhecido');
      
      // Fallback para temas padrão em caso de erro
      setThemeTypes([
        {
          id: 1,
          key: 'amor',
          name: 'Amor',
          description: 'Descubra os segredos do seu coração e encontre respostas sobre relacionamentos, paixões e conexões profundas.',
          icon: 'Heart',
          gradient: 'from-pink-500/20 to-rose-500/20',
          is_active: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        },
        {
          id: 2,
          key: 'saude',
          name: 'Saúde',
          description: 'Obtenha orientações sobre bem-estar físico, mental e espiritual para uma vida mais equilibrada e saudável.',
          icon: 'Leaf',
          gradient: 'from-green-500/20 to-emerald-500/20',
          is_active: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        },
        {
          id: 3,
          key: 'dinheiro',
          name: 'Dinheiro',
          description: 'Explore oportunidades financeiras, investimentos e caminhos para a prosperidade e abundância material.',
          icon: 'DollarSign',
          gradient: 'from-yellow-500/20 to-orange-500/20',
          is_active: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchThemeTypes();

    // Listener para mudanças nos tipos de tema
    const handleThemeTypesUpdate = () => {
      console.log('Atualizando tipos de tema...');
      fetchThemeTypes();
    };

    // Escutar eventos customizados para atualização
    window.addEventListener('themeTypesUpdated', handleThemeTypesUpdate);
    
    // Escutar mudanças no localStorage como fallback
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'themeTypesLastUpdate') {
        handleThemeTypesUpdate();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('themeTypesUpdated', handleThemeTypesUpdate);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const refetch = () => {
    fetchThemeTypes();
  };

  return {
    themeTypes,
    isLoading,
    error,
    refetch
  };
};